import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { X, ShieldCheck, Loader2, CreditCard, QrCode, Lock, Sparkles, Eye } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { computeZodiac } from "../lib/astrology";
import { useLang } from "../context/LanguageContext";
import { CODE_TO_URL } from "../lib/i18nRouter";
import { CONSULTATION_FEE_DISPLAY, CONSULTATION_WINDOW_MIN } from "../lib/consultationConfig";

/**
 * "Get Professional Consultation · Rp 20.000" paywall + checkout-init modal.
 *
 * Renders inside the existing Gemologist assistant when the user clicks the
 * upsell button. On submit → POST /api/consultation/checkout → redirects to
 * Stripe Checkout. After payment, Stripe redirects to /:lang/consultation/success.
 */
const ConsultationPaywall = ({ onClose, gemstoneId = null }) => {
  const { apiLang, lang } = useLang();
  const slug = CODE_TO_URL[lang] || "en";
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [agree, setAgree] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const preview = useMemo(() => (birthDate ? computeZodiac(birthDate) : null), [birthDate]);

  const submit = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email so we can deliver your reading.");
      return;
    }
    if (!agree) {
      toast.error("Please accept the consultation terms.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/consultation/checkout", {
        email,
        name: name || undefined,
        birth_date: birthDate || undefined,
        gemstone_id: gemstoneId || undefined,
        lang: apiLang || "en",
        origin_url: window.location.origin,
      });
      if (data?.url) {
        // Stash the consultation_id locally so we can pick it back up on /success
        // even if the Stripe redirect strips query params
        localStorage.setItem("opik_consult_pending", data.consultation_id);
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout. Please try again.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="consult-paywall"
      className="fixed inset-0 z-[95] bg-black/90 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="max-w-3xl mx-auto my-8 md:my-12 bg-noir border border-gold/40 shadow-[0_0_80px_rgba(212,175,55,0.18)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* HEADER with premium badge */}
        <div className="flex items-start justify-between p-6 border-b border-white/10">
          <div className="flex-1 pr-4">
            <div className="inline-flex items-center gap-1.5 bg-gold/15 border border-gold/40 text-gold font-accent text-[9px] tracking-[0.24em] uppercase px-2.5 py-1 mb-3">
              <ShieldCheck size={10} /> Professional Gemologist Consultation
            </div>
            <h2 className="font-heading text-3xl text-ivory">Consult a Senior Gemologist</h2>
          </div>
          <button
            data-testid="consult-paywall-close"
            onClick={onClose}
            className="text-white/60 hover:text-white p-2"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          {/* DESCRIPTION + INCLUSIONS */}
          <div className="p-6 md:p-8 border-b md:border-b-0 md:border-r border-white/10">
            <p className="text-white/65 text-sm leading-relaxed mb-6">
              A one-on-one professional reading from our Senior Gemologist. Submit one detailed question
              after payment and receive a thorough, narrated answer in your chosen language.
            </p>

            <ul className="space-y-3 mb-8" data-testid="consult-inclusions">
              {[
                "Zodiac gemstone advice",
                "Birthstone analysis",
                "Gem authenticity guidance",
                "Investment recommendation",
                "Jewelry matching advice",
              ].map((label) => (
                <li key={label} className="flex items-start gap-3 text-white/80 text-sm">
                  <span className="mt-1 w-1.5 h-1.5 bg-gold flex-shrink-0" />
                  {label}
                </li>
              ))}
            </ul>

            <div className="border border-gold/30 bg-black/30 p-5">
              <div className="font-accent text-[10px] tracking-[0.24em] uppercase text-gold mb-2">Consultation Fee</div>
              <div className="font-heading text-4xl text-ivory">{CONSULTATION_FEE_DISPLAY}</div>
              <div className="text-white/40 text-xs mt-2">
                One detailed answer · {CONSULTATION_WINDOW_MIN} min access window after payment
              </div>
            </div>
          </div>

          {/* FORM */}
          <div className="p-6 md:p-8">
            <label className="block mb-4">
              <span className="text-white/50 text-[10px] tracking-[0.24em] font-accent uppercase block mb-2">Your Name (Optional)</span>
              <input
                data-testid="consult-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Aria Wijaya"
                className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-white focus:outline-none focus:border-gold"
              />
            </label>
            <label className="block mb-4">
              <span className="text-white/50 text-[10px] tracking-[0.24em] font-accent uppercase block mb-2">Email *</span>
              <input
                data-testid="consult-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-white focus:outline-none focus:border-gold"
              />
            </label>
            <label className="block mb-5">
              <span className="text-white/50 text-[10px] tracking-[0.24em] font-accent uppercase block mb-2">Birth Date (Optional)</span>
              <input
                data-testid="consult-dob"
                type="date"
                value={birthDate}
                onChange={(e) => setBirthDate(e.target.value)}
                max={new Date().toISOString().slice(0, 10)}
                className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-white focus:outline-none focus:border-gold"
              />
            </label>

            {preview && (
              <div data-testid="consult-preview" className="border border-gold/30 bg-black/40 p-3 mb-5 text-xs">
                <div className="font-accent text-[9px] tracking-[0.22em] uppercase text-gold mb-1">Provisional</div>
                <div className="text-white/70">{preview.name} · {preview.element} · Birthstone {preview.birthstone}</div>
              </div>
            )}

            {/* Payment methods */}
            <div className="font-accent text-[10px] tracking-[0.24em] uppercase text-white/50 mb-3">Payment Method</div>
            <div className="space-y-2 mb-5" data-testid="consult-payment-methods">
              <PaymentRow
                icon={<CreditCard size={14} />}
                label="Credit / Debit Card"
                sub="Visa · Mastercard · Amex via Stripe"
                active
              />
              <PaymentRow icon={<QrCode size={14} />} label="QRIS" sub="Coming soon" disabled />
              <PaymentRow icon={<Sparkles size={14} />} label="PayPal" sub="Coming soon" disabled />
            </div>

            <label className="flex items-start gap-2.5 mb-5 cursor-pointer text-white/60 text-xs leading-relaxed">
              <input
                data-testid="consult-agree"
                type="checkbox"
                checked={agree}
                onChange={(e) => setAgree(e.target.checked)}
                className="mt-1 accent-[#D4AF37]"
              />
              <span>
                I understand this is a one-shot professional reading. I will receive a single thorough answer
                within {CONSULTATION_WINDOW_MIN} minutes of payment.
              </span>
            </label>

            <button
              data-testid="consult-submit"
              onClick={submit}
              disabled={submitting}
              className="w-full flex items-center justify-center gap-2 bg-gold text-black font-accent text-[11px] tracking-[0.28em] uppercase py-4 hover:bg-ivory transition-colors duration-500 disabled:opacity-50"
            >
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Lock size={13} />}
              {submitting ? "Redirecting…" : `Pay ${CONSULTATION_FEE_DISPLAY} & Consult`}
            </button>
            <p className="text-white/30 text-[10px] tracking-[0.18em] uppercase font-accent mt-3 text-center">
              Secured by Stripe · 256-bit TLS
            </p>

            {/* Free preview / demo */}
            <Link
              data-testid="consult-demo-link"
              to={`/${slug}/consultation/success?demo=1`}
              onClick={onClose}
              className="mt-4 flex items-center justify-center gap-2 text-white/50 hover:text-gold font-accent text-[10px] tracking-[0.24em] uppercase transition-colors"
            >
              <Eye size={11} /> Preview the experience (free demo)
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const PaymentRow = ({ icon, label, sub, active = false, disabled = false }) => (
  <div
    className={`flex items-center justify-between border px-3.5 py-3 transition-colors ${
      active ? "border-gold bg-gold/5" : "border-white/15"
    } ${disabled ? "opacity-50" : ""}`}
  >
    <div className="flex items-center gap-3">
      <span className={active ? "text-gold" : "text-white/40"}>{icon}</span>
      <div>
        <div className="text-white/90 text-sm">{label}</div>
        <div className="text-white/40 text-[10px] tracking-[0.18em] uppercase font-accent">{sub}</div>
      </div>
    </div>
    {active && <span className="w-2 h-2 rounded-full bg-gold" />}
  </div>
);

export default ConsultationPaywall;
